import React, { ReactElement } from 'react';
import { Button, Collapse, CollapseProps, Flex, Space, Tag, Typography } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { useStore } from 'zustand';

// eslint-disable-next-line no-restricted-imports
import { useCreateRegionalCalendar } from 'features/regionalCalendar/create';

import { isHasAccess } from 'features/validateAccess';
import {
  CountryWithCalendarsDTO,
  calendarStore,
  DayTypeIcon,
  getEventTypeIcon,
  NATIONAL_CALENDAR_NAME,
} from 'entities/calendar';

import { ReactComponent as AddIcon } from 'shared/assets/icons/dynamicColor/add-icon.svg';
import { formatDateRange } from 'shared/lib/dayjs';
import { pathKeys } from 'shared/lib/react-router';
import { LinkButton } from 'shared/ui/LinkButton';
import { CREATE_CALENDAR, UPDATE_CALENDAR } from '../../CalendarsTable.permissions';
import { EditRegionalCalendarButton } from '../EditRegionalCalendarButton';
import { EmptyCalendarsList } from '../EmptyCalendarsList';

import styles from './CalendarsList.module.scss';

const { Text } = Typography;

const STANDARD_WORKING_HOURS = 8;

export const CalendarsList = ({ record }: { record: CountryWithCalendarsDTO }): ReactElement => {
  const { t } = useTranslation('Calendar');

  const { calendars } = record;
  const { searchQuery } = useStore(calendarStore);
  const defaultActiveKey = searchQuery.length >= 2 ? calendars.map((calendar) => calendar.id) : undefined;

  const openCreateModal = useCreateRegionalCalendar(record.id);

  const canUpdateCalendar = isHasAccess(UPDATE_CALENDAR);
  const canCreateCalendar = isHasAccess(CREATE_CALENDAR);

  const items: CollapseProps['items'] = calendars.map((calendar) => ({
    key: calendar.id,
    label: (
      <Flex align="center" justify="space-between">
        <Space>
          <Text className={styles.calendarName}>
            {calendar?.name === NATIONAL_CALENDAR_NAME
              ? t('calendarLayout.nationalCalendar.defaultName.text')
              : calendar?.name}
          </Text>
          <DayTypeIcon
            type="NON_WORKING_DAY"
            title={t('calendarsTable.expandedList.tags.nonWorking', { count: calendar.nonWorkingDays })}
          />
          <DayTypeIcon
            type="WORKING_DAY"
            title={t('calendarsTable.expandedList.tags.working', { count: calendar.workingDays })}
          />
        </Space>
        <div className={styles.calendarActions}>
          {calendar.name !== NATIONAL_CALENDAR_NAME && canUpdateCalendar && (
            <EditRegionalCalendarButton calendar={calendar} />
          )}
          <LinkButton
            linkProps={{
              to: pathKeys.calendars.calendarLayout({ calendarId: calendar.id }),
            }}
            buttonProps={{ className: styles.calendarLayoutBtn }}
          >
            {t('calendarList.setCalendarBtn.text')}
          </LinkButton>
        </div>
      </Flex>
    ),
    children: (
      <Space direction="vertical" className={styles.calendarContentWrapper}>
        <div className={classNames(styles.calendarEventList, { [styles.withBorder]: !!calendar.events.length })}>
          {calendar.events.length ? (
            calendar.events.map((event) => {
              return (
                <div key={event.startDate} className={styles.eventBlock}>
                  <div className={styles.eventDetailsColumn}>
                    {getEventTypeIcon(event.exclusionType)}
                    <div>{formatDateRange({ startDate: event.startDate, endDate: event.endDate })}</div>
                  </div>
                  <div className={styles.eventActionsColumn}>
                    <span>{event.name}</span>
                    {event.exclusionType === 'WORKING_DAY' && event.workingHours !== STANDARD_WORKING_HOURS ? (
                      <Tag color="blue" bordered className={styles.countTag}>
                        <span>
                          {event.workingHours}
                          {t('calendarsTable.CalendarsList.hours.text')}
                        </span>
                      </Tag>
                    ) : null}
                  </div>
                </div>
              );
            })
          ) : (
            <EmptyCalendarsList calendarId={calendar.id} />
          )}
        </div>
      </Space>
    ),
  }));

  return (
    <React.Fragment>
      <Collapse
        destroyOnHidden
        items={items}
        ghost
        collapsible="icon"
        className={styles.calendarCollapseWrapper}
        defaultActiveKey={defaultActiveKey}
      />
      {canCreateCalendar && (
        <Button type="primary" className={styles.addCalendarBtn} onClick={openCreateModal}>
          <AddIcon className={styles.addIcon} /> {t('calendarList.createRegionalCalendarBtn.text')}
        </Button>
      )}
    </React.Fragment>
  );
};
